<script setup>
import Dialog from 'primevue/dialog'
import { ref, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { pushDialogLiveRegions } from '@/util/live_announcer'
const props = defineProps({
  dialogId: {
    type: String,
    required: true
  },
  visible: {
    type: Boolean,
    required: false
  },
  header: {
    type: String,
    required: false
  },
  showHeaderContent: {
    type: Boolean,
    default: false
  },
  footerButtons: {
    type: Boolean,
    default: false
  },
  footerClasses: {
    type: String,
    default: ''
  },
  showCloseIcon: {
    type: Boolean,
    default: true
  },
  dismissableMask: {
    type: Boolean,
    default: true
  },
  closeOnEscape: {
    type: Boolean,
    default: true
  },
  pt: {
    type: Object,
    default: () => ({})
  }
})

const isVisible = ref(props.visible)
const emit = defineEmits(['update:visible', 'hide'])
watch(
  () => props.visible,
  (newVal) => {
    isVisible.value = newVal
  }
)
watch(isVisible, (newVal) => {
  emit('update:visible', newVal)
})

function handleEscapeKey(event) {
  if (event.key === 'Escape' && props.closeOnEscape && isVisible.value) {
    isVisible.value = false
    emit('hide')
  }
}

// Announcing into the body-level live regions while this dialog is open can go
// unheard, since aria-modal="true" hides everything outside the open dialog from
// assistive tech. Give this dialog its own regions and register them while open,
// so announce() (see @/util/live_announcer) can target them directly.
const politeRegionEl = ref(null)
const assertiveRegionEl = ref(null)
let unregisterLiveRegions = null
let isMounted = false

function registerLiveRegions() {
  if (!isMounted || !isVisible.value || unregisterLiveRegions || !politeRegionEl.value || !assertiveRegionEl.value) return
  unregisterLiveRegions = pushDialogLiveRegions({
    polite: politeRegionEl.value,
    assertive: assertiveRegionEl.value
  })
}

function unregisterLiveRegionsIfAny() {
  unregisterLiveRegions?.()
  unregisterLiveRegions = null
}

onMounted(async () => {
  isMounted = true
  document.addEventListener('keydown', handleEscapeKey)
  if (isVisible.value) {
    await nextTick()
    registerLiveRegions()
  }
})

onUnmounted(() => {
  isMounted = false
  document.removeEventListener('keydown', handleEscapeKey)
  unregisterLiveRegionsIfAny()
})

watch(isVisible, async (visible) => {
  if (visible) {
    await nextTick()
    registerLiveRegions()
  } else {
    unregisterLiveRegionsIfAny()
  }
})
</script>
<template>
  <Dialog
    :id="dialogId"
    v-model:visible="isVisible"
    modal
    class="w-auto md:w-[718px]"
    :draggable="false"
    :dismissableMask="dismissableMask"
    :blockScroll="true"
    position="top"
    :closable="showCloseIcon"
    :closeOnEscape="closeOnEscape"
    :pt="pt"
  >
    <div ref="politeRegionEl" class="sr-only" role="status" aria-live="polite" aria-atomic="true"></div>
    <div ref="assertiveRegionEl" class="sr-only" role="alert" aria-live="assertive" aria-atomic="true"></div>
    <div class="px-6 pb-6 pt-4">
      <slot name="bodyContent" />
    </div>
    <div v-if="footerButtons" :class="['flex justify-end gap-2 border-t border-t-gray-300 px-6 pt-4 items-center', footerClasses]">
      <slot name="footerButtonLeft" />
      <slot name="footerButtonRight" />
    </div>
    <template #closeicon>
      <span class="material-icons-round">close</span>
    </template>
    <template #header v-if="header">
      <h2 class="text-xl font-bold leading-7" :id="dialogId + '_header'">{{ header }}</h2>
    </template>
    <template #header v-else-if="showHeaderContent">
        <slot name="headerContent" />
    </template>
  </Dialog>
</template>